import React, { useEffect, useRef, useState } from "react";
import { api } from "../../../software/api";
import { Search as SearchIcon, Plus, ArrowRight, Loader2, Phone, MapPin, Wrench, ScrollText, FileText, ShieldCheck, Package, Hash } from "lucide-react";

const TYPES = [
  ["all", "All"],
  ["client", "Client"],
  ["work_order", "Work Order"],
  ["quote", "Quote"],
  ["invoice", "Invoice"],
  ["warranty", "Warranty"],
  ["parts", "Parts"],
  ["serial", "Serial No."],
];

// non-client record groups rendered as simple rows (icon + title + subtitle)
const REC_GROUPS = [
  ["work_orders", "Work Orders", Wrench, "text-amber-700 bg-amber-50 border-amber-200"],
  ["quotes", "Quotes", ScrollText, "text-blue-700 bg-blue-50 border-blue-200"],
  ["invoices", "Invoices", FileText, "text-emerald-700 bg-emerald-50 border-emerald-200"],
  ["warranties", "FM 84 Warranties", ShieldCheck, "text-indigo-700 bg-indigo-50 border-indigo-200"],
  ["parts", "Parts Orders", Package, "text-cyan-700 bg-cyan-50 border-cyan-200"],
  ["serials", "Spa Serials (J No.)", Hash, "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200"],
];

const PALETTE = ["bg-sky-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500", "bg-indigo-500", "bg-cyan-600", "bg-orange-500", "bg-violet-500"];
const initialsOf = (s) => (s || "").trim().split(/[\s\-]+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

function ClientCard({ display, company, phone, address, onClick, testid }) {
  const colour = PALETTE[(display.charCodeAt(0) || 0) % PALETTE.length];
  return (
    <button onClick={onClick} data-testid={testid}
      className="group w-full text-left rounded-xl border border-slate-200 bg-white p-3.5 hover:border-sky-400 hover:shadow-md transition-all flex items-center gap-4">
      <span className={`h-12 w-12 shrink-0 rounded-xl ${colour} text-white font-display font-extrabold text-lg flex items-center justify-center shadow`}>{initialsOf(display)}</span>
      <span className="min-w-0 flex-1">
        <span className="block font-display font-extrabold text-base text-slate-900 truncate">{display}</span>
        {company && company !== display && <span className="block text-xs text-slate-400 truncate">{company}</span>}
        <span className="flex flex-wrap items-center gap-x-4 gap-y-0.5 mt-0.5 text-[12px] text-slate-500">
          {phone && <span className="flex items-center gap-1 truncate"><Phone className="h-3.5 w-3.5 text-slate-400 shrink-0" />{phone}</span>}
          {address && <span className="flex items-center gap-1 truncate" title={address}><MapPin className="h-3.5 w-3.5 text-slate-400 shrink-0" />{address}</span>}
        </span>
      </span>
      <ArrowRight className="h-5 w-5 text-slate-300 group-hover:text-sky-500 shrink-0" />
    </button>
  );
}

export default function Search({ onNewClient, onOpenResult, initialType = "all" }) {
  const [q, setQ] = useState("");
  const [type, setType] = useState(initialType);
  const [res, setRes] = useState(null);       // /search response when q present
  const [browse, setBrowse] = useState([]);   // all-clients list when q empty
  const [loading, setLoading] = useState(true);
  const timer = useRef(null);
  const seq = useRef(0);
  const [loyalty, setLoyalty] = useState("");
  const [loyErr, setLoyErr] = useState("");
  const [loyBusy, setLoyBusy] = useState(false);

  const goLoyalty = async () => {
    const v = loyalty.trim();
    if (!v) return;
    setLoyBusy(true); setLoyErr("");
    try {
      const list = await api("GET", `/clients?q=${encodeURIComponent(v)}&limit=50`);
      const arr = Array.isArray(list) ? list : [];
      const norm = (x) => String(x || "").trim().replace(/^0+/, "");
      const exact = arr.find((c) => String(c.loyalty_number || "").trim() === v)
        || (/^\d+$/.test(v) && arr.find((c) => c.loyalty_number && norm(c.loyalty_number) === norm(v)));
      if (exact) { onOpenResult({ ...exact, client_id: exact.id, tab: "water_tests" }); }
      else setLoyErr(`No client with loyalty number “${v}”.`);
    } catch { setLoyErr("Search failed — please try again."); }
    finally { setLoyBusy(false); }
  };

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const mine = ++seq.current;
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        if (!q.trim()) {
          const list = await api("GET", `/clients?q=`);
          if (mine === seq.current) { setBrowse(Array.isArray(list) ? list : []); setRes(null); }
        } else {
          const d = await api("GET", `/search?q=${encodeURIComponent(q.trim())}&type=${type}`);
          if (mine === seq.current) setRes(d);
        }
      } catch (e) {
        if (mine === seq.current) { setRes({ total: 0 }); setBrowse([]); }
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, type]);

  const showClients = type === "all" || type === "client";
  const searchClients = res?.clients || [];
  const total = res?.total || 0;

  return (
    <div data-testid="search-panel" className="max-w-5xl mx-auto">
      {/* header */}
      <div className="rounded-t-2xl px-5 py-4 text-white shadow-lg flex items-center justify-between gap-4" style={{ background: "linear-gradient(180deg,#2f558f,#1e3a63)" }}>
        <h1 className="font-display text-2xl font-extrabold flex items-center gap-2.5 shrink-0"><SearchIcon className="h-6 w-6" /> Search the Database</h1>
        <div className="flex-1 flex justify-center">
          <div className="w-full max-w-xs">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
              <input
                value={loyalty}
                onChange={(e) => { setLoyalty(e.target.value); setLoyErr(""); }}
                onKeyDown={(e) => { if (e.key === "Enter") goLoyalty(); }}
                placeholder="Loyalty Number"
                inputMode="numeric"
                data-testid="search-loyalty-input"
                className="w-full rounded-xl border-2 border-amber-300 bg-white pl-10 pr-10 py-2.5 text-[15px] font-bold text-slate-800 placeholder:text-slate-400 focus:border-amber-400 focus:ring-2 focus:ring-amber-200 outline-none shadow-inner"
              />
              {loyBusy && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-sky-600 animate-spin" />}
            </div>
            {loyErr && <p className="mt-1 text-[12px] font-semibold text-amber-200" data-testid="search-loyalty-error">{loyErr}</p>}
          </div>
        </div>
        <button onClick={onNewClient} data-testid="search-add-client"
          className="shrink-0 rounded-xl bg-green-500 hover:bg-green-600 text-white font-extrabold px-5 py-2.5 flex items-center gap-2 shadow">
          <Plus className="h-5 w-5" /> + New Client
        </button>
      </div>

      <div className="rounded-b-2xl border-x border-b border-[#2f558f]/30 bg-[#1e3a63] px-4 pt-4 pb-4">
        {/* big yellow input */}
        <div className="relative">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
          {loading && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-sky-600 animate-spin" />}
          <input
            autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-omni-input"
            placeholder="Type a name, company, phone or email…"
            className="w-full rounded-xl border-2 border-amber-300 bg-amber-50 pl-12 pr-11 py-3.5 text-[16px] font-medium text-slate-800 placeholder:text-slate-400 focus:border-amber-400 focus:ring-2 focus:ring-amber-200 outline-none shadow-inner"
          />
        </div>
        {/* filter chips */}
        <div className="flex flex-wrap gap-2 mt-3">
          {TYPES.map(([k, l]) => (
            <button key={k} onClick={() => setType(k)} data-testid={`search-type-${k}`}
              className={`px-4 py-1.5 rounded-full text-[13px] font-bold border transition-colors ${type === k ? "bg-[#12284a] text-white border-[#12284a]" : "bg-white text-slate-700 border-white/70 hover:bg-slate-100"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* results */}
      <div className="mt-4 space-y-2.5" data-testid="search-results">
        {/* empty query -> browse all clients */}
        {!q.trim() && browse.map((c) => {
          const display = (c.name || `${c.first_name || ""} ${c.surname || ""}`.trim() || "Client").replace(/^[\s\u2013-]+/, "").trim() || "Client";
          const a = c.address || {};
          const addr = [a.street, a.suburb, a.state, a.postcode].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
          return <ClientCard key={c.id} display={display} company={c.company} phone={c.phone} address={addr}
            testid={`search-result-clients-${c.id}`}
            onClick={() => onOpenResult({ ...c, client_id: c.id, tab: "client_details" })} />;
        })}
        {!q.trim() && !loading && browse.length === 0 && <p className="text-slate-500 text-sm p-6 text-center">No clients yet — tap “+ New Client” to add one.</p>}

        {/* active query -> client cards + record rows */}
        {q.trim() && !loading && total === 0 && <p className="text-slate-500 text-sm p-6 text-center" data-testid="search-no-results">No matches for “{q}”. Try a different term or filter.</p>}

        {q.trim() && showClients && searchClients.map((r) => {
          const display = (`${r.first_name || ""} ${r.surname || ""}`.trim() || r.title || "Client").replace(/^[\s\u2013-]+/, "").trim() || "Client";
          return <ClientCard key={r.id} display={display} company={r.company} phone={r.phone} address={r.full_address}
            testid={`search-result-clients-${r.id}`} onClick={() => onOpenResult(r)} />;
        })}

        {q.trim() && res && REC_GROUPS.map(([key, label, Icon, cls]) => {
          const items = res[key] || [];
          if (!items.length) return null;
          return (
            <div key={key} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 flex items-center gap-2">
                <span className={`h-6 w-6 rounded-md border flex items-center justify-center ${cls}`}><Icon className="h-3.5 w-3.5" /></span>
                <span className="text-[12px] font-bold text-slate-700 uppercase tracking-wide">{label}</span>
                <span className="text-[11px] text-slate-400">({items.length})</span>
              </div>
              {items.map((r) => (
                <button key={r.id} onClick={() => onOpenResult(r)} data-testid={`search-result-${key}-${r.id}`}
                  className="group w-full text-left flex items-center gap-3 px-3 py-2.5 hover:bg-sky-50 border-b border-slate-50 last:border-b-0">
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-slate-800 truncate">{r.title}</span>
                    {r.subtitle && <span className="block text-[12px] text-slate-400 truncate">{r.subtitle}</span>}
                  </span>
                  <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-sky-500 shrink-0" />
                </button>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
